import React, { useState, useEffect } from "react";
import { variantSelect, productSelect } from "@/store";
import {
  usePdpActions,
  usePdpSellingPlan,
  usePdpProduct,
  usePdpVariant,
} from "./PdpContext";
import { Button } from "@/components/Button";
import { CartAddLineItem, shopifyCartApi } from "@/services/shopifyCart";
import { useCartUiActions } from "../cart";
import {
  SubscriptionSelector,
  SubscriptionSelectorValue,
} from "@/features/product/SubscriptionSelector";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { getFinalAtcCtaPrice } from "@/utils/atcCTAPrice";

import { AddToCartProps, VariantSizeOptionsGroup } from "./AddToCart";

export const AddToCartProductCard = ({
  cartSubmitId,
  addToCartText = "Add to Cart",
  outOfStockText = "Out of Stock",
  onAddToCartSuccess,
}: AddToCartProps) => {
  const shopifyCartAdder = shopifyCartApi.useTrackedAdd();
  const cartActions = useCartUiActions();
  const variant = usePdpVariant();
  const actions = usePdpActions();
  const subscription = variantSelect.orderableSubscription(variant);
  const product = usePdpProduct();
  const sellingPlan = usePdpSellingPlan();

  const isBundle =
    product?.tags?.includes("bundle") &&
    !product.handle.includes("starter-kit");

  // State for subscription toggle
  const [subToggle, setSubToggle] = useState<SubscriptionSelectorValue>(
    sellingPlan ? "subscription" : "one-time"
  );
  
  // Track selected variant index for quantity-based adds
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);

  // Calculate the subscription price using the centralized logic
  const subscriptionPrice = variantSelect.lowestPrice(variant, product);

  // Define the displayed price based on subscription toggle
  const displayedPrice =
    subToggle === "subscription" ? subscriptionPrice : variant.price;

  // Use the new promo price utility
  const atcCTAPrice = getFinalAtcCtaPrice({
    productCollections: product.collections,
    basePriceCents: displayedPrice.asCents(),
    sellingPlan,
    subscription,
    variant,
    product,
    currency: "$",
    strikeThroughSize: "text-xs",
    priceSize: "text-sm",
    bannerPadding: "px-3 py-2",
  });

  const addToCartPrice = (
    <>
      {variant?.price != undefined ? (
        <div className="flex flex-wrap items-center space-x-2 lg:justify-end">
          <p
            data-id="product-card-price"
            className={`body ml-1 text-xs font-semibold text-white ${
              variant?.size || variant?.subscription ? "order-2" : ""
            }`}
          >
            <span className="sr-only">Current price</span>
            {atcCTAPrice.display}
          </p>
        </div>
      ) : null}
    </>
  );

  // Update subscription preference when subToggle changes
  useEffect(() => {
    actions.setSubscriptionPreference(subToggle === "subscription");
  }, [subToggle, actions]);

  // Sync selectedVariantIndex when variant changes externally (e.g., URL params)
  useEffect(() => {
    const currentIndex = product.variants.findIndex(v => v.id === variant.id);
    if (currentIndex !== -1) {
      setSelectedVariantIndex(currentIndex);
    }
  }, [variant.id, product.variants]);

  // Find the base variant (tiered_discount_variant_multiplier === 1)
  // Check both direct property and metafields (for backward compatibility)
  const baseVariant = product.variants.find(
    v => (v.tiered_discount_variant_multiplier ?? v.metafields?.tiered_discount_variant_multiplier) === 1
  );
  
  // Get the selected variant's tiered_discount_variant_multiplier
  // Check both direct property and metafields (for backward compatibility)
  const selectedVariantMultiplier = variant.tiered_discount_variant_multiplier ?? variant.metafields?.tiered_discount_variant_multiplier;
  
  // Determine which variant and quantity to use based on tiered_discount_variant_multiplier
  let variantToAdd = variant;
  let quantity = 1;
  
  // Check if the metafield exists and has a value (not null/undefined)
  if (selectedVariantMultiplier !== null && selectedVariantMultiplier !== undefined) {
    // Dynamic tiered discount logic
    if (selectedVariantMultiplier === 1) {
      // Base variant - add 1 quantity of itself
      variantToAdd = variant;
      quantity = 1;
    } else if (selectedVariantMultiplier >= 2) {
      // Tiered variant - add multiplier quantity of base variant
      if (baseVariant) {
        variantToAdd = baseVariant;
        quantity = selectedVariantMultiplier;
      } else {
        // Fallback if base variant not found
        variantToAdd = variant;
        quantity = 1;
      }
    }
  } else {
    // When tiered_discount_variant_multiplier is null or undefined, use normal behavior
    // Add 1 quantity of the selected variant (no special logic)
    variantToAdd = variant;
    quantity = 1;
  }
  
  // Determine the subscription plan for the variant being added
  const variantToAddSubscription = variantSelect.orderableSubscription(variantToAdd);
  const sellingPlanToUse = subToggle === "subscription" && variantToAddSubscription
    ? (sellingPlan || productSelect.defaultSellingPlan(product))
    : undefined;

  return (
    <>
      {(variant?.size || variant?.subscription || variant?.price) && (
        <div className="plp-box-sizes_variant-options mt-4">
          <VariantSizeOptionsGroup onVariantIndexChange={setSelectedVariantIndex} />
        </div>
      )}

      <div className="w-full">
        {subscription && (
          <div className="pt-3">
            <SubscriptionSelector
              value={subToggle}
              onChange={(value) => {
                setSubToggle(value);
              }}
              size="sm"
              cta={
                <p className="body w-full text-center text-xs">
                  <span>
                    {variantSelect.subscriptionDiscount(variant, product) >
                    0 ? (
                      <>
                        Save{" "}
                        {variantSelect.subscriptionDiscount(variant, product)}%
                        when you subscribe.
                      </>
                    ) : null}{" "}
                    Ships every 4 months.
                  </span>
                </p>
              }
            />
          </div>
        )}

        <div className="mt-4 flex w-full flex-col-reverse items-center justify-between space-x-0 lg:mt-1 lg:space-y-2">
          <Button
            className="learn-more-card-link mt-1 items-center space-x-2"
            component="a"
            size="sm"
            variant="ghost"
            href={`/products/${product.handle}?variant=${variant.id}`}
            aria-label={`Learn more about ${product.title}`}
          >
            <InfoOutlined className="h-4 w-4 text-inherit" />{" "}
            <span>More details</span>
          </Button>
          <Button
            id={cartSubmitId}
            className="lg:btn-sm xl:btn-base btn-base w-full"
            disabled={!variant.available}
            onClick={() => {
              const lineItem: CartAddLineItem[] = [
                variantSelect.cartAddLineItem(variantToAdd, {
                  subscription: sellingPlanToUse || false,
                  quantity,
                }),
              ];
              const sourceModule = isBundle
                ? "bundle_plp_quick_add"
                : "plp_quick_add";
              const body = { items: lineItem, sourceModule };
              shopifyCartAdder.mutate(body, {
                onSuccess: () => {
                  cartActions.openDrawer();
                  if (onAddToCartSuccess) {
                    onAddToCartSuccess();
                  }
                },
              });
            }}
          >
            <div className="flex w-full items-center justify-between text-xs">
              <span>{variant.available ? addToCartText : outOfStockText}</span>
              {addToCartPrice}
            </div>
          </Button>
        </div>
        {!variant.available && (
          <div className="basis-full pt-4">
            <Button
              id="BIS_trigger"
              className="flex w-full"
              aria-label="notify me when restocked"
              size="lg"
            >
              Notify Me When Restocked
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
